import { Button } from "react-bootstrap";
import { base64URLEncode, generateRandomString } from "./util";
import { useEffect, useState } from "react";
import { ReactComponent as Connect } from "assets/svg/connect.svg";
import { ReactComponent as Disconnect } from "assets/svg/disconnect.svg";
import completeZoomOAuth from "services/zoom/completeZoomOAuth";
import revokeZoomAccess from "services/zoom/revokeZoomAccess";
import Loading from "components/Loading";
import ErrorMessage from "components/error-message";
import queryZoomIntegrationStatus from "services/zoom/queryZoomIntegrationStatus";
import { IS_DEV_ENV } from "constants/isDevEnv";
import ConfirmModal from "components/confirm-modal";
import styles from "./Integration.module.scss";

const ZoomIntegration = () => {
  const [isIntegrationEnabled, setIsIntegrationEnabled] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>();
  const [connecting, setConnecting] = useState<boolean>(false);

  const [showConfirmDisconnect, setShowConfirmDisconnect] =
    useState<boolean>(false);

  const [showConnectModal, setShowConnectModal] = useState<boolean>(false);

  useEffect(() => {
    const checkConnectionStatus = async () => {
      try {
        const { is_connected: isConnected } =
          await queryZoomIntegrationStatus();
        setIsLoading(false);
        setIsIntegrationEnabled(isConnected);
      } catch (err) {
        console.warn(err);
        setError(
          new Error(
            "Unable to verify Zoom connection status. Please try again later",
          ),
        );
      }
    };
    if (!connecting) {
      checkConnectionStatus();
    }
  }, [setIsIntegrationEnabled, connecting]);

  const onDisconnect = async () => {
    setIsLoading(true);
    try {
      await revokeZoomAccess();
      setIsIntegrationEnabled(false);
      setShowConfirmDisconnect(false);
    } catch (err) {
      setError(new Error("Unable to disconnect Zoom. Please try again later"));
    } finally {
      setIsLoading(false);
    }
  };

  const onConnect = async () => {
    const codeVerifier = generateRandomString(128);

    sessionStorage.setItem("codeVerifier", codeVerifier);

    const digest = await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(codeVerifier),
    );
    const codeChallenge = base64URLEncode(digest);

    const zoomAuthorizationEndpoint = "https://zoom.us/oauth/authorize";

    const clientId = IS_DEV_ENV
      ? "8QnSiTtSHOfaKckYSN3A"
      : "na8aOlmAQmSZW4DtbzWVw";

    const redirectUri = IS_DEV_ENV
      ? "https://orchidsquirrel.com/pro-dashboard/settings/integrations/zoom"
      : "https://orchid.exchange/pro-dashboard/settings/integrations/zoom";

    window.location.href = `${zoomAuthorizationEndpoint}?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&code_challenge=${codeChallenge}&code_challenge_method=S256`;
  };

  useEffect(() => {
    const relayAuthCode = async () => {
      // This means that this page was arrived at via a redirect back from Zoom.
      // Time to request and get the access/refresh tokens via the server
      const codeVerifier = sessionStorage.getItem("codeVerifier");
      const code = query.get("code");
      if (codeVerifier && code) {
        setConnecting(true);
        setIsLoading(true);
        try {
          await completeZoomOAuth(code, codeVerifier);
          setIsIntegrationEnabled(true);
        } catch (err) {
          console.warn(err);
          setError(
            new Error(
              "Unable to establish connection with Zoom. Please try again later",
            ),
          );
        } finally {
          setIsLoading(false);
          setConnecting(false);
        }
      } else {
        setError(
          new Error("Unable to connect with Zoom. Please try again later"),
        );
      }
    };

    const query = new URLSearchParams(window.location.search);
    if (query.get("code")) {
      relayAuthCode();
    }
  }, []);

  let integrationDetails;
  if (isLoading) {
    integrationDetails = <Loading />;
  } else if (isIntegrationEnabled) {
    integrationDetails = (
      <Button
        variant={"outline-primary"}
        className={styles["integration__gcal-disconnect-button"]}
        onClick={() => setShowConfirmDisconnect(true)}
      >
        <Disconnect />
        Disconnect
      </Button>
    );
  } else {
    integrationDetails = (
      <Button
        className={styles["integration__gcal-connect-button"]}
        onClick={() => setShowConnectModal(true)}
      >
        <Connect />
        Connect
      </Button>
    );
  }

  return (
    <>
      {error && (
        <ErrorMessage
          show={true}
          onHide={() => setError(null)}
          errorMsg={error?.toString()}
        />
      )}
      {integrationDetails}

      {showConfirmDisconnect && (
        <ConfirmModal
          show={showConfirmDisconnect}
          onHide={() => setShowConfirmDisconnect(false)}
          onCancel={() => setShowConfirmDisconnect(false)}
          onConfirm={() => onDisconnect()}
          labelTitle={"Disconnect Zoom"}
          children={"Are you sure you want to disconnect Zoom?"}
          labelCancel={"Cancel"}
          labelConfirm={"Disconnect"}
          footerClass={styles["confirm-delete__footer"]}
        />
      )}

      {showConnectModal && (
        <ConfirmModal
          show={showConnectModal}
          onHide={() => setShowConnectModal(false)}
          onCancel={() => setShowConnectModal(false)}
          onConfirm={() => onConnect()}
          labelTitle={"Connect Zoom"}
          children={
            <>
              <p>Allow Orchid to manage video calls for your sessions.</p>
              <p>
                By signing with Zoom, you <b>acknowledge and agree</b> to follow{" "}
                <b>HIPAA compliance requirements</b> including{" "}
                <b>protecting sensitive information</b>.
              </p>
            </>
          }
          labelCancel={"Cancel"}
          labelConfirm={"Sign in with Zoom"}
        />
      )}
    </>
  );
};

export default ZoomIntegration;
